import React, { useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import { ActionCommentDTO } from '@behavior/activity/dto/ActionCommentDTO';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';
import usePostComment from '@behavior/activity/usePostComment';
import useMarkActionAsCompleted from '@behavior/activity/useMarkActionAsCompleted';
import { Activity } from '@src/routes';
import { Mode } from '@src/constants';
import ShareButton from './components/ShareButton';

const INK = '#111418';
const TEXT = '#1f2328';
const TEXT_SECONDARY = '#5b6470';
const TEXT_MUTED = '#8b95a3';
const BORDER = '#e6e8ec';
const BORDER_HOVER = '#d4d8df';
const SURFACE = '#ffffff';
const SURFACE_SUBTLE = '#f7f8fa';

const PENDING_FG = '#b45309';
const PENDING_ACCENT = '#f59e0b';
const PENDING_BG = '#fff7ed';
const PENDING_BG_TILE = '#fed7aa';

const DONE_FG = '#047857';
const DONE_ACCENT = '#10b981';
const DONE_BG = '#ecfdf5';
const DONE_BG_TILE = '#a7f3d0';

const maskPrivate = (s: string): string => s.replace(/[a-z]/g, 'x');

const fullName = (p?: PersonDTO, showPersonalData = true): string => {
  if (!p) {
    return '—';
  }
  const raw =
    (p.name && p.name.trim()) ||
    [p.firstName, p.lastName].filter(Boolean).join(' ').trim() ||
    '';
  if (!raw) {
    return '—';
  }
  return showPersonalData ? raw : maskPrivate(raw);
};

const initials = (p?: PersonDTO): string => {
  if (!p) {
    return '?';
  }
  const first = (p.firstName?.[0] ?? p.name?.[0] ?? '').toUpperCase();
  const last = (p.lastName?.[0] ?? '').toUpperCase();
  const combined = `${first}${last}`;
  return combined || '?';
};

const formatDate = (d?: string | Date, locale?: string): string => {
  if (!d) {
    return '—';
  }
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return '—';
  }
  return dt.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (d?: string | Date, locale?: string): string => {
  if (!d) {
    return '—';
  }
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return '—';
  }
  return dt.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatTimeOnly = (d?: string | Date, locale?: string): string => {
  if (!d) {
    return '';
  }
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return '';
  }
  return dt.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatRelative = (d?: string | Date, locale?: string): string => {
  if (!d) {
    return '';
  }
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return '';
  }
  const ms = dt.getTime() - Date.now();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const months = Math.round(days / 30);
    if (Math.abs(months) >= 12) {
      return rtf.format(Math.round(months / 12), 'year');
    }
    if (Math.abs(days) >= 30) {
      return rtf.format(months, 'month');
    }
    return rtf.format(days, 'day');
  } catch {
    return '';
  }
};

const avatarColor = (p?: PersonDTO): string => {
  if (!p?.id) {
    return '#dfe3ea';
  }
  const palette = ['#e0e7ff', '#dcfce7', '#fef3c7', '#fce7f3', '#dbeafe', '#fee2e2', '#e6e8ec'];
  return palette[p.id % palette.length];
};

type StatusBannerProps = {
  pending: boolean;
  primaryLabel: string;
  primaryRelative?: string;
  detail: React.ReactNode;
  icon: React.ReactNode;
  action?: React.ReactNode;
};
function StatusBanner({
  pending,
  primaryLabel,
  primaryRelative,
  detail,
  icon,
  action,
}: StatusBannerProps) {
  const fg = pending ? PENDING_FG : DONE_FG;
  const accent = pending ? PENDING_ACCENT : DONE_ACCENT;
  const bg = pending ? PENDING_BG : DONE_BG;
  const tile = pending ? PENDING_BG_TILE : DONE_BG_TILE;
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: bg,
        border: `1px solid ${tile}`,
        borderLeft: `5px solid ${accent}`,
        borderRadius: '10px',
        px: { xs: 1.75, sm: 2.25 },
        py: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.75} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            bgcolor: tile,
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: fg,
                letterSpacing: '-0.005em',
                lineHeight: 1.2,
              }}
            >
              {primaryLabel}
            </Typography>
            {primaryRelative && (
              <Typography sx={{ fontSize: 13, color: fg, opacity: 0.85 }}>
                {primaryRelative}
              </Typography>
            )}
          </Stack>
          <Typography
            sx={{
              fontSize: 13,
              color: TEXT_SECONDARY,
              mt: 0.25,
              lineHeight: 1.45,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {detail}
          </Typography>
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
    </Box>
  );
}
StatusBanner.defaultProps = { primaryRelative: undefined, action: undefined };

type MetaCellProps = {
  icon: React.ReactNode;
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  isLast?: boolean;
};
function MetaCell({ icon, label, primary, secondary, isLast }: MetaCellProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: { xs: 1.75, sm: 2 },
        py: 1.5,
        borderBottom: {
          xs: isLast ? 'none' : `1px solid ${BORDER}`,
          md: 'none',
        },
        borderRight: {
          xs: 'none',
          md: isLast ? 'none' : `1px solid ${BORDER}`,
        },
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.875 }}>
        <Box sx={{ display: 'flex', color: TEXT_MUTED }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: TEXT_MUTED,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Box sx={{ mb: secondary ? 0.25 : 0, minWidth: 0 }}>{primary}</Box>
      {secondary && (
        <Box
          sx={{
            fontSize: 12.5,
            color: TEXT_SECONDARY,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {secondary}
        </Box>
      )}
    </Box>
  );
}
MetaCell.defaultProps = { secondary: undefined, isLast: false };

type Props = {
  action: ActionDTO;
  showPersonalData?: boolean;
};

function ActionDetailView({ action, showPersonalData }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const locale = i18n.language;
  const [text, setText] = useState('');
  const [composerFocused, setComposerFocused] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isMarking, setIsMarking] = useState(false);

  const postComment = usePostComment(action.id ?? 0);
  const markAsCompleted = useMarkActionAsCompleted();

  const isCompleted = Boolean(action.completionDte);
  const sortedComments = (action.comments ?? [])
    .slice()
    .sort((a, b) => new Date(b.createDte).getTime() - new Date(a.createDte).getTime());

  const handlePost = () => {
    const description = text.trim();
    if (!description || isPosting) {
      return;
    }
    setIsPosting(true);
    const newComment: ActionCommentDTO = {
      description,
      createDte: Date.now().toString(),
    };
    postComment(newComment)
      .then(() => setText(''))
      .catch(() => {
        /* error toast handled by usePostComment */
      })
      .finally(() => setIsPosting(false));
  };

  const handleMarkCompleted = () => {
    if (!action.id || isMarking) {
      return;
    }
    setIsMarking(true);
    markAsCompleted(action.id);
  };

  const markCompletedButton = (
    <Button
      onClick={handleMarkCompleted}
      disabled={isMarking}
      disableElevation
      startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
      sx={{
        bgcolor: INK,
        color: '#ffffff',
        fontSize: 13,
        fontWeight: 500,
        px: 1.5,
        py: 0.7,
        borderRadius: '6px',
        border: `1px solid ${INK}`,
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: '#000000' },
        '&:disabled': {
          bgcolor: SURFACE_SUBTLE,
          color: TEXT_MUTED,
          borderColor: BORDER,
        },
      }}
    >
      {t('action.mark.as.completed')}
    </Button>
  );

  const openActivityButton = action.activityId != null && (
    <Button
      onClick={() =>
        history.push(
          Activity.children.activityDetails.route(String(action.activityId), Mode.VIEW)
        )
      }
      disableElevation
      startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
      sx={{
        bgcolor: SURFACE,
        color: TEXT_SECONDARY,
        fontSize: 13,
        fontWeight: 500,
        px: 1.5,
        py: 0.7,
        borderRadius: '6px',
        border: `1px solid ${BORDER}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
          bgcolor: SURFACE_SUBTLE,
          color: INK,
          borderColor: BORDER_HOVER,
        },
      }}
    >
      {t('action.list.openActivity')}
    </Button>
  );

  return (
    <Box sx={{ bgcolor: SURFACE, minHeight: '100%', py: { xs: 3, md: 4 } }}>
      <Container maxWidth="lg" sx={{ maxWidth: { md: 1032 } }}>
        <Stack spacing={2.5}>
          {isCompleted ? (
            <StatusBanner
              pending={false}
              icon={<CheckCircleRoundedIcon sx={{ fontSize: 22 }} />}
              primaryLabel={t('action.detail.banner.completed')}
              primaryRelative={formatRelative(action.completionDte, locale)}
              detail={
                <>
                  {t('action.detail.banner.on')}{' '}
                  <Box component="span" sx={{ color: TEXT, fontWeight: 600 }}>
                    {formatDateTime(action.completionDte, locale)}
                  </Box>{' '}
                  {t('action.detail.banner.by')}{' '}
                  <Box component="span" sx={{ color: TEXT, fontWeight: 600 }}>
                    {fullName(action.assignedUser, showPersonalData)}
                  </Box>
                </>
              }
            />
          ) : (
            <StatusBanner
              pending
              icon={<HourglassEmptyRoundedIcon sx={{ fontSize: 22 }} />}
              primaryLabel={t('action.not.yet.completed')}
              primaryRelative={
                action.deadlineDte
                  ? `${t('action.detail.banner.due')} ${formatRelative(action.deadlineDte, locale)}`
                  : undefined
              }
              detail={
                action.deadlineDte ? (
                  <>
                    {t('action.detail.banner.deadline')}{' '}
                    <Box component="span" sx={{ color: TEXT, fontWeight: 600 }}>
                      {formatDate(action.deadlineDte, locale)}
                    </Box>
                  </>
                ) : (
                  t('action.detail.banner.awaiting')
                )
              }
              action={markCompletedButton}
            />
          )}

          <Box>
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 700,
                  letterSpacing: '-0.015em',
                  color: TEXT,
                  lineHeight: 1.15,
                }}
              >
                Action&nbsp;
                <Box component="span" sx={{ color: TEXT_MUTED, fontWeight: 600 }}>
                  #{action.id}
                </Box>
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <ShareButton />
                {openActivityButton}
              </Stack>
            </Stack>
            <Typography
              sx={{
                fontSize: 15.5,
                lineHeight: 1.55,
                color: action.description ? TEXT : TEXT_MUTED,
                fontStyle: action.description ? 'normal' : 'italic',
                whiteSpace: 'pre-wrap',
              }}
            >
              {action.description || t('action.detail.no.description')}
            </Typography>
            {(((action.locations?.length ?? 0) > 0) || action.outOfBuilding) && (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                <PlaceOutlinedIcon
                  sx={{ fontSize: 16, color: TEXT_MUTED, flexShrink: 0 }}
                />
                {action.locations?.map((loc, i) => (
                  <Box
                    key={`loc-${i}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 24,
                      px: 1.125,
                      borderRadius: '6px',
                      bgcolor: SURFACE_SUBTLE,
                      border: `1px solid ${BORDER}`,
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: TEXT,
                    }}
                  >
                    {loc}
                  </Box>
                ))}
                {action.outOfBuilding && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      height: 24,
                      px: 1,
                      borderRadius: '6px',
                      bgcolor: PENDING_FG,
                      color: '#ffffff',
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                    }}
                  >
                    <WarningAmberRoundedIcon sx={{ fontSize: 12 }} />
                    {t('action.list.outsideBuilding')}
                  </Box>
                )}
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: '10px',
              bgcolor: SURFACE,
              overflow: 'hidden',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch">
              <MetaCell
                icon={<PersonOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                label={t(
                  'action.form.details.page.container.action.required.specify.resolver'
                )}
                primary={
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        fontSize: 11,
                        fontWeight: 600,
                        color: TEXT,
                        bgcolor: avatarColor(action.assignedUser),
                        border: `1px solid ${BORDER}`,
                      }}
                    >
                      {initials(action.assignedUser)}
                    </Avatar>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: TEXT,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fullName(action.assignedUser, showPersonalData)}
                    </Typography>
                  </Stack>
                }
                secondary={
                  showPersonalData && action.assignedUser?.email
                    ? action.assignedUser.email
                    : undefined
                }
              />
              <MetaCell
                icon={<EventOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t(
                  'action.form.details.page.container.action.required.specify.date.for.deadline'
                )}
                primary={
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: TEXT }}>
                    {formatDate(action.deadlineDte, locale)}
                  </Typography>
                }
                secondary={!isCompleted ? formatRelative(action.deadlineDte, locale) : undefined}
              />
              <MetaCell
                icon={<HistoryEduOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t('action.detail.created')}
                isLast
                primary={
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: TEXT }}>
                    {formatDate(action.createDte, locale)}
                    <Box
                      component="span"
                      sx={{ color: TEXT_SECONDARY, fontWeight: 400, ml: 0.75 }}
                    >
                      {formatTimeOnly(action.createDte, locale)}
                    </Box>
                  </Typography>
                }
                secondary={`${t('action.detail.created.by')} ${fullName(action.createUser, showPersonalData)}`}
              />
            </Stack>
          </Box>

          <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: TEXT }}>
                {t('action.report.details.comments.title')}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: TEXT_MUTED }}>
                {sortedComments.length}
              </Typography>
            </Stack>

            <Box
              sx={{
                border: `1px solid ${composerFocused ? INK : BORDER}`,
                borderRadius: '10px',
                bgcolor: SURFACE,
                transition: 'border-color 120ms ease',
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <TextField
                multiline
                minRows={2}
                maxRows={10}
                fullWidth
                placeholder={t('action.detail.composer.placeholder')}
                value={text}
                onChange={e => setText(e.target.value)}
                disabled={isPosting}
                onFocus={() => setComposerFocused(true)}
                onBlur={() => setComposerFocused(false)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: TEXT,
                    px: 1.75,
                    py: 1.25,
                    '& textarea::placeholder': { color: TEXT_MUTED, opacity: 1 },
                  },
                }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderTop: `1px solid ${BORDER}`,
                  bgcolor: SURFACE_SUBTLE,
                }}
              >
                <Typography sx={{ fontSize: 12, color: TEXT_MUTED, pl: 0.5 }}>
                  {t('action.detail.add.comment')}
                </Typography>
                <Button
                  onClick={handlePost}
                  disabled={!text.trim() || isPosting}
                  disableElevation
                  sx={{
                    bgcolor: INK,
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 500,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '6px',
                    border: `1px solid ${INK}`,
                    '&:hover': { bgcolor: '#000000' },
                    '&:disabled': {
                      bgcolor: SURFACE,
                      color: TEXT_MUTED,
                      borderColor: BORDER,
                    },
                  }}
                >
                  {isPosting ? t('action.detail.posting') : t('action.detail.post.comment')}
                </Button>
              </Stack>
            </Box>

            {sortedComments.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${BORDER}`,
                  borderRadius: '8px',
                  py: 2.5,
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 13, color: TEXT_MUTED }}>
                  {t('action.detail.no.comments')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {sortedComments.map((c, idx) => {
                  const relative = formatRelative(c.createDte, locale);
                  const absolute = formatDateTime(c.createDte, locale);
                  return (
                    <Box
                      key={c.id ?? `c-${idx}`}
                      sx={{
                        border: `1px solid ${BORDER}`,
                        borderRadius: '8px',
                        bgcolor: SURFACE,
                        px: 1.75,
                        py: 1.25,
                        transition: 'border-color 120ms ease, background-color 120ms ease',
                        '&:hover': {
                          borderColor: BORDER_HOVER,
                          bgcolor: SURFACE_SUBTLE,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: 11,
                            fontWeight: 600,
                            color: TEXT,
                            bgcolor: avatarColor(c.createUser),
                            border: `1px solid ${BORDER}`,
                            flexShrink: 0,
                            mt: '1px',
                          }}
                        >
                          {initials(c.createUser)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="baseline"
                            flexWrap="wrap"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography
                              sx={{ fontSize: 13.5, fontWeight: 600, color: TEXT }}
                            >
                              {fullName(c.createUser, showPersonalData)}
                            </Typography>
                            {relative && (
                              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                                {relative}
                              </Typography>
                            )}
                            <Typography
                              component="span"
                              sx={{ fontSize: 12, color: TEXT_MUTED }}
                              title={absolute}
                            >
                              · {absolute}
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: TEXT,
                              lineHeight: 1.55,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {c.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

ActionDetailView.defaultProps = { showPersonalData: false };

export default ActionDetailView;
